import { useState } from "react";
import { Lightbulb } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

const TYPES = [
  { value: "WALK", label: "Weg vorschlagen" },
  { value: "CHALLENGE", label: "Challenge vorschlagen" },
  { value: "GAME", label: "Spiel vorschlagen" },
  { value: "OTHER", label: "Etwas anderes" },
];

export function IdeaBlock({ communityId, allowed = ["WALK", "CHALLENGE", "GAME"] }) {
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ title: "", location_text: "", description: "", image: null });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.title.trim()) {
      setError("Bitte gib deiner Idee einen Titel.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/ideas", { ...form, community_id: communityId, idea_type: open });
      setDone(true);
      setOpen(null);
      setForm({ title: "", location_text: "", description: "", image: null });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-12 rounded-3xl border bg-white p-6" data-testid="idea-block">
      <div className="flex items-center gap-2">
        <Lightbulb className="h-5 w-5" style={{ color: "var(--brand-accent)" }} />
        <h2 className="text-2xl">Hast du eine Idee für unsere Gemeinde?</h2>
      </div>
      <p className="mt-2 text-sm text-[var(--ink-soft)]">
        Wir lesen jeden Vorschlag. Auch kleine Ideen verändern einen Ort.
      </p>
      {done ? (
        <p className="mt-4 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm" data-testid="idea-success">
          Danke. Deine Idee ist bei der Gemeinde eingelangt.
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {TYPES.filter((t) => allowed.includes(t.value)).map((t) => (
          <button
            key={t.value}
            className={`btn ${open === t.value ? "btn-primary" : "btn-ghost"} !py-2.5 !text-xs`}
            onClick={() => {
              setDone(false);
              setOpen(open === t.value ? null : t.value);
            }}
            data-testid={`idea-open-${t.value.toLowerCase()}`}
          >
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {open ? (
        <form onSubmit={submit} className="mt-5 space-y-4 rise" data-testid="idea-form">
          <div>
            <div className="eyebrow mb-1">Titel</div>
            <input
              className="field"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="idea-title"
            />
          </div>
          <div>
            <div className="eyebrow mb-1">Wo könnte das stattfinden?</div>
            <input
              className="field"
              value={form.location_text}
              onChange={(e) => setForm({ ...form, location_text: e.target.value })}
              data-testid="idea-location"
            />
          </div>
          <div>
            <div className="eyebrow mb-1">Beschreib deine Idee</div>
            <textarea
              className="field min-h-[120px]"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="idea-description"
            />
          </div>
          <ImageUpload value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="Bild (optional)" testId="idea-image" />
          {error ? <p className="text-sm text-red-700" data-testid="idea-error">{error}</p> : null}
          <button className="btn btn-primary" disabled={busy} data-testid="idea-submit">
            IDEE ABSENDEN
          </button>
        </form>
      ) : null}
    </section>
  );
}

export default IdeaBlock;
